import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { Badge } from '../../../components/ui/badge'
import { Input } from '../../../components/ui/input'
import { Button } from '../../../components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

interface Organization {
  id: number
  name: string
  current_plan: string
}

interface Filters {
  plan: string
}

interface User {
  id: number
  email: string
  first_name: string
  last_name: string
  admin: boolean
  super_admin?: boolean
  organization_role: string
  activated: boolean
  messages_count: number
  created_at: string
  organization?: Organization
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface AdminUsersIndexProps {
  users: User[]
  pagy: Pagination
  filters: Filters
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
    notice?: string
  }
}

function Index({ users, pagy, filters }: AdminUsersIndexProps) {
  const { t } = useTranslation('admin')
  const [searchEmail, setSearchEmail] = useState('')
  const [searchFirstName, setSearchFirstName] = useState('')
  const [searchLastName, setSearchLastName] = useState('')

  const buildParams = (overrides: Record<string, string | null> = {}) => {
    const params: Record<string, string> = {}

    // Preserve search filters
    if (searchEmail) params['q[email_cont]'] = searchEmail
    if (searchFirstName) params['q[first_name_cont]'] = searchFirstName
    if (searchLastName) params['q[last_name_cont]'] = searchLastName

    // Preserve current filters
    if (filters.plan) params['plan'] = filters.plan

    // Apply overrides
    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }

    return params
  }

  const handleSearch = () => {
    const params: Record<string, string> = {}

    if (searchEmail) params['q[email_cont]'] = searchEmail
    if (searchFirstName) params['q[first_name_cont]'] = searchFirstName
    if (searchLastName) params['q[last_name_cont]'] = searchLastName
    if (filters.plan) params['plan'] = filters.plan

    router.get('/admin/users', params, { preserveState: true })
  }

  const handleReset = () => {
    setSearchEmail('')
    setSearchFirstName('')
    setSearchLastName('')
    // Reset to defaults: paid users only
    router.get('/admin/users', { plan: 'paid' }, { preserveState: true })
  }

  const handlePlanChange = (value: string) => {
    router.get('/admin/users', buildParams({ plan: value }), { preserveState: true })
  }

  const handleSort = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    // Toggle between asc and desc
    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) {
      newSort = `${field} desc`
    }

    const searchParams: Record<string, string> = {}

    // Preserve existing search filters
    if (searchEmail) searchParams['q[email_cont]'] = searchEmail
    if (searchFirstName) searchParams['q[first_name_cont]'] = searchFirstName
    if (searchLastName) searchParams['q[last_name_cont]'] = searchLastName
    if (filters.plan) searchParams['plan'] = filters.plan

    // Add sort parameter
    searchParams['q[s]'] = newSort

    router.get('/admin/users', searchParams, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const params = new URLSearchParams(window.location.search)
    const currentSort = params.get('q[s]')

    if (currentSort === `${field} asc`) {
      return '↑'
    } else if (currentSort === `${field} desc`) {
      return '↓'
    }
    return '⇅'
  }

  return (
    <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col py-2">
            <div className="px-3">
              {/* Compact Filter Bar */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-sm font-semibold whitespace-nowrap">
                  {t('users.index.titleWithCount', { count: pagy.count })}
                </span>
                <div className="w-px h-4 bg-border" />
                <Select value={filters.plan || 'paid'} onValueChange={handlePlanChange}>
                  <SelectTrigger className="h-7 text-xs w-28">
                    <SelectValue placeholder={t('users.index.table.plan')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">{t('users.index.filters.plan.paidOnly')}</SelectItem>
                    <SelectItem value="free">{t('users.index.filters.plan.free')}</SelectItem>
                    <SelectItem value="standard">{t('users.index.filters.plan.standard')}</SelectItem>
                    <SelectItem value="pro">{t('users.index.filters.plan.pro')}</SelectItem>
                    <SelectItem value="team">{t('users.index.filters.plan.team')}</SelectItem>
                    <SelectItem value="all">{t('users.index.filters.plan.all')}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="w-px h-4 bg-border" />
                <Input
                  type="text"
                  placeholder={t('users.index.filters.firstNamePlaceholder')}
                  className="h-7 text-xs w-28"
                  value={searchFirstName}
                  onChange={(e) => setSearchFirstName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Input
                  type="text"
                  placeholder={t('users.index.filters.lastNamePlaceholder')}
                  className="h-7 text-xs w-28"
                  value={searchLastName}
                  onChange={(e) => setSearchLastName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Input
                  type="text"
                  placeholder={t('users.index.filters.emailPlaceholder')}
                  className="h-7 text-xs w-40"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                  {t('users.index.buttons.search')}
                </Button>
                <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                  {t('users.index.buttons.reset')}
                </Button>
                <div className="w-px h-4 bg-border" />
                <Button
                  onClick={() => router.visit('/admin/users/new')}
                  className="h-7 px-2 text-xs"
                  variant="default"
                >
                  {t('users.index.buttons.createUser')}
                </Button>
              </div>

              {/* Users Table */}
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)', minHeight: '400px' }}>
                  <table className="w-full">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('first_name')}
                        >
                          <div className="flex items-center gap-1">
                            {t('users.index.table.user')}
                            <span className="text-gray-400">{getSortIcon('first_name')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('users.index.table.plan')}
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('email')}
                        >
                          <div className="flex items-center gap-1">
                            {t('users.index.table.email')}
                            <span className="text-gray-400">{getSortIcon('email')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('users.index.table.organization')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('users.index.table.role')}
                        </th>
                        <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                          {t('users.index.table.status')}
                        </th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                          {t('users.index.table.messages')}
                        </th>
                        <th
                          className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                          onClick={() => handleSort('created_at')}
                        >
                          <div className="flex items-center gap-1">
                            {t('users.index.table.joined')}
                            <span className="text-gray-400">{getSortIcon('created_at')}</span>
                          </div>
                        </th>
                        <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                          {t('users.index.table.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {users.length > 0 ? (
                        users.map((user) => (
                          <tr key={user.id} className="hover:bg-muted transition-colors">
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm font-medium text-foreground">
                                {user.first_name} {user.last_name}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {user.organization?.current_plan ? (
                                <Badge
                                  variant={user.organization.current_plan === 'free' ? 'secondary' : 'default'}
                                  className="text-xs px-1.5 py-0.5"
                                >
                                  {user.organization.current_plan.charAt(0).toUpperCase() + user.organization.current_plan.slice(1)}
                                </Badge>
                              ) : (
                                <span className="text-sm text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">{user.email}</span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {user.organization ? user.organization.name : '-'}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {user.super_admin ? (
                                <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-800">
                                  {t('users.index.badges.superAdmin')}
                                </Badge>
                              ) : (
                                <Badge variant={user.organization_role === 'external' ? 'outline' : 'secondary'} className="text-xs px-1.5 py-0.5">
                                  {user.organization_role === 'admin' ? t('users.index.badges.orgAdmin') : user.organization_role === 'external' ? t('users.index.badges.external') : t('users.index.badges.member')}
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {user.activated ? (
                                <Badge variant="success" className="text-xs px-1.5 py-0.5">{t('users.index.badges.activated')}</Badge>
                              ) : (
                                <Badge variant="warning" className="text-xs px-1.5 py-0.5">{t('users.index.badges.pending')}</Badge>
                              )}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right text-sm text-muted-foreground">
                              {user.messages_count}
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="text-sm text-foreground">
                                {new Date(user.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 whitespace-nowrap text-right">
                              <Link
                                href={`/admin/users/${user.id}`}
                                className="text-sm text-blue-600 hover:text-blue-900"
                              >
                                {t('users.index.table.view')}
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="px-3 py-8 text-center text-sm text-muted-foreground">
                            {t('users.index.empty')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Compact Pagination */}
                {pagy.pages > 1 && (
                  <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                    <span className="text-muted-foreground">
                      {pagy.from}-{pagy.to} of {pagy.count}
                    </span>
                    <div className="flex gap-1">
                      {pagy.prev && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/users', { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                        >
                          {t('users.index.pagination.previous')}
                        </Button>
                      )}
                      {pagy.next && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => router.get('/admin/users', { ...buildParams(), page: pagy.next }, { preserveState: true })}
                        >
                          {t('users.index.pagination.next')}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  )
}




Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
